/* assets/gift-box-toggle.js
   "Add a Gift Box?" toggle for the cart drawer.

   Mirrors assets/shipping-protection.js's add/remove-on-checkbox-change pattern and
   uses the same <mini-cart> API (getSectionsToRender(), renderContents()), but stays
   pure opt-in: unlike shipping protection, this starts unchecked and never auto-adds
   itself — it only enters the cart when the shopper actively checks the box, and
   leaves the moment they uncheck it.

   Also owns the "Your gift message" textarea nested inside it: shown only while the
   box is checked. Writes straight to cart.note (the same field the checkout's order
   note uses, and the same one fulfillment already checks) rather than a separate
   custom attribute, so a message written here is guaranteed to reach the warehouse
   the same way a checkout note would. A visible "Save message" button is the primary
   way to commit it (with a brief "Saved" confirmation) since blur/typing alone isn't
   an obvious action to a shopper; blur and debounced-while-typing saves stay on too
   as a safety net in case they go straight to checkout without clicking it. Collapses
   to a one-line "message added" summary once saved, so it doesn't sit open blocking
   the rest of the cart. */
(function () {
  class GiftBoxToggle extends HTMLElement {
    connectedCallback() {
      this.miniCart = document.querySelector('mini-cart');
      this.check = this.querySelector('[data-dt-giftwrap-check]');
      this.variantId = this.dataset.variantId;
      this.inCart = this.dataset.inCart === 'true';
      this.messageWrap = this.querySelector('[data-dt-giftwrap-message]');
      this.messageForm = this.querySelector('[data-dt-giftwrap-message-form]');
      this.messageSummary = this.querySelector('[data-dt-giftwrap-message-summary]');
      this.messageSummaryText = this.querySelector('.DT-giftwrap__message-summary-text');
      this.messageEdit = this.querySelector('[data-dt-giftwrap-message-edit]');
      this.messageInput = this.querySelector('[data-dt-giftwrap-message-input]');
      this.messageSaveBtn = this.querySelector('[data-dt-giftwrap-message-save]');
      if (!this.check || !this.variantId) return;

      this.check.checked = this.inCart;
      this.toggleMessage(this.inCart);
      this.syncMessageView();

      this.check.addEventListener('change', () => {
        this.check.checked ? this.add() : this.remove();
      });

      if (this.messageSaveBtn) {
        this.messageSaveBtn.addEventListener('click', () => this.saveMessage(false, true));
      }
      if (this.messageInput) {
        this.messageInput.addEventListener('blur', () => this.saveMessage());
        if (typeof debounce === 'function') {
          this.messageInput.addEventListener('input', debounce(() => this.saveMessage(true), 600));
        }
      }
      if (this.messageEdit) {
        this.messageEdit.addEventListener('click', () => {
          this.showForm();
          this.messageInput && this.messageInput.focus();
        });
      }
    }

    // Collapsed "✓ message added" summary vs. the open textarea, based on current value.
    syncMessageView() {
      var hasValue = this.messageInput && this.messageInput.value.trim() !== '';
      if (hasValue) this.showSummary(); else this.showForm();
    }

    showSummary() {
      if (!this.messageSummary || !this.messageForm) return;
      this.messageForm.setAttribute('hidden', '');
      this.messageSummary.removeAttribute('hidden');
      if (this.messageSummaryText && this.messageInput) {
        this.messageSummaryText.textContent = '✓ ' + this.messageInput.value.trim();
      }
    }

    showForm() {
      if (!this.messageSummary || !this.messageForm) return;
      this.messageSummary.setAttribute('hidden', '');
      this.messageForm.removeAttribute('hidden');
    }

    toggleMessage(show) {
      if (!this.messageWrap) return;
      if (show) {
        this.messageWrap.removeAttribute('hidden');
      } else {
        this.messageWrap.setAttribute('hidden', '');
      }
    }

    // keepOpen: true while actively typing (debounced autosave) — don't collapse yet.
    // viaButton: true when triggered by the explicit Save button — show a brief
    // "Saved" confirmation on the button itself before collapsing to the summary.
    saveMessage(keepOpen, viaButton) {
      if (!this.messageInput) return;
      var value = this.messageInput.value;
      var body = JSON.stringify({ note: value });
      var url = (window.theme && theme.routes && theme.routes.cart_update_url) ? theme.routes.cart_update_url : '/cart/update.js';

      if (viaButton && this.messageSaveBtn) {
        this.messageSaveBtn.disabled = true;
        this.messageSaveBtn.textContent = 'Saving…';
      }

      fetch(url, { ...fetchConfig(), ...{ body } }).then(() => {
        if (viaButton && this.messageSaveBtn) {
          this.messageSaveBtn.textContent = 'Saved ✓';
          setTimeout(() => {
            if (this.messageSaveBtn) {
              this.messageSaveBtn.disabled = false;
              this.messageSaveBtn.textContent = 'Save message';
            }
            if (!keepOpen) this.syncMessageView();
          }, 600);
          return;
        }
        if (!keepOpen) this.syncMessageView();
      });
    }

    sections() {
      if (this.miniCart && typeof this.miniCart.getSectionsToRender === 'function') {
        return this.miniCart.getSectionsToRender().map((s) => s.id);
      }
      return [];
    }

    setLoading(on) {
      this.classList.toggle('is-loading', on);
      if (this.check) this.check.disabled = on;
    }

    render(response) {
      this.miniCart && this.miniCart.renderContents(response);
    }

    add() {
      if (this.inCart) return;
      this.setLoading(true);
      this.toggleMessage(true);

      var body = JSON.stringify({
        items: [{ id: this.variantId, quantity: 1 }],
        sections: this.sections(),
        sections_url: window.location.pathname
      });
      var url = (window.theme && theme.routes && theme.routes.cart_add_url) ? theme.routes.cart_add_url : '/cart/add.js';

      fetch(url, { ...fetchConfig(), ...{ body } })
        .then((r) => r.json())
        .then((response) => {
          this.setLoading(false);
          if (response.status) { // add error (e.g. sold out / unavailable)
            if (this.check) this.check.checked = false;
            this.toggleMessage(false);
            return;
          }
          this.inCart = true;
          this.render(response);
        })
        .catch(() => {
          this.setLoading(false);
          if (this.check) this.check.checked = false;
          this.toggleMessage(false);
        });
    }

    remove() {
      this.setLoading(true);
      this.toggleMessage(false);

      var body = JSON.stringify({
        id: String(this.variantId),
        quantity: 0,
        sections: this.sections(),
        sections_url: window.location.pathname
      });
      var url = (window.theme && theme.routes && theme.routes.cart_change_url) ? theme.routes.cart_change_url : '/cart/change.js';

      fetch(url, { ...fetchConfig(), ...{ body } })
        .then((r) => r.json())
        .then((response) => {
          this.setLoading(false);
          this.inCart = false;
          this.render(response);
        })
        .catch(() => {
          this.setLoading(false);
          if (this.check) this.check.checked = true;
          this.toggleMessage(true);
        });
    }
  }

  if (!customElements.get('gift-box-toggle')) {
    customElements.define('gift-box-toggle', GiftBoxToggle);
  }
})();
